import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import logger from '../utils/logger';
import { useNotification } from './NotificationContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = React.memo(({ children, supabase }) => {
  const { showError, showSuccess } = useNotification();

  // Core auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    bio: '',
    location: '',
    seller_terms: 'Standard marketplace terms apply. Contact seller for specific details.',
    is_public: true,
    show_collection_count: true,
    avatar_url: '',
  });

  // Modal state (extracted from App.js)
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  // Initialize auth state and set up listener
  // Function to create user profile if it doesn't exist
  const ensureUserProfile = useCallback(
    async user => {
      if (!user) return;

      try {
        // Check if profile already exists
        const { data: existingProfile, error: checkError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error('Error checking user profile:', checkError);
          return;
        }

        if (!existingProfile) {
          const username = user.user_metadata?.username || user.email?.split('@')[0] || 'user';

          const { error: insertError } = await supabase.from('user_profiles').insert({
            user_id: user.id,
            username: username,
            email: user.email,
            display_name: user.user_metadata?.display_name || username,
            is_public: user.user_metadata?.is_public ?? true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

          if (insertError) {
            console.error('Error creating user profile:', insertError);
          }
        }
      } catch (error) {
        console.error('Error in ensureUserProfile:', error);
      }
    },
    [supabase]
  );

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      setCurrentUser(session?.user ?? null);
      setLoading(false);

      // Create profile after setting user state to avoid blocking auth
      if (session?.user) {
        ensureUserProfile(session.user).catch(err => {
          console.error('Profile creation failed but auth continues:', err);
        });
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setCurrentUser(session?.user ?? null);

      // Create profile after setting user state to avoid blocking auth
      if (session?.user) {
        ensureUserProfile(session.user).catch(err => {
          console.error('Profile creation failed but auth continues:', err);
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase, ensureUserProfile]);

  const checkAdminStatus = useCallback(async () => {
    if (!currentUser) return;

    logger.debug('Checking admin status', { userId: currentUser.id });

    const { data, error } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', currentUser.id)
      .limit(1);

    logger.debug('Admin check result', { hasData: !!data, hasError: !!error });

    if (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } else {
      logger.debug('Setting admin status', { isAdmin: !!(data && data.length > 0) });
      setIsAdmin(!!(data && data.length > 0));
    }
  }, [currentUser, supabase]);

  // Reset admin status when user changes
  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  }, [currentUser, checkAdminStatus]);

  // Function to check if user should see profile completion modal
  const shouldShowProfileCompletion = useCallback(
    async userId => {
      if (!userId) return false;

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error checking profile completion:', error);
          return false;
        }

        // Show profile completion if user has a temporary username (starts with "user_")
        const hasTemporaryUsername = data.username && data.username.startsWith('user_');

        return hasTemporaryUsername;
      } catch (err) {
        console.error('Error checking profile completion:', err);
        return false;
      }
    },
    [supabase]
  );

  // Function to mark profile completion as shown
  const markProfileCompletionShown = useCallback(
    async userId => {
      if (!userId) return;

      try {
        await supabase
          .from('user_profiles')
          .update({ profile_completion_shown: true })
          .eq('user_id', userId);
      } catch (err) {
        console.error('Error marking profile completion as shown:', err);
      }
    },
    [supabase]
  );

  const handleLogin = useCallback(
    async e => {
      e.preventDefault();
      const { error } = await supabase.auth.signInWithPassword({
        email: authForm.email,
        password: authForm.password,
      });

      if (error) {
        console.error('Login error:', error);
        showError('Login failed. Please check your credentials.');
        return { success: false };
      } else {
        setAuthForm({
          email: '',
          password: '',
          confirmPassword: '',
          username: '',
          bio: '',
          location: '',
          seller_terms: 'Standard marketplace terms apply. Contact seller for specific details.',
          is_public: true,
          show_collection_count: true,
          avatar_url: '',
        });
        setShowAuthModal(false); // Close modal on successful login
        return { success: true };
      }
    },
    [supabase, authForm, setAuthForm, showError]
  );

  const handlePasswordReset = useCallback(
    async email => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        console.error('Password reset error:', error);
        showError('Failed to send password reset email. Please try again.');
        return { success: false };
      } else {
        showSuccess('Password reset email sent! Check your inbox.');
        return { success: true };
      }
    },
    [supabase, showError, showSuccess]
  );

  const handleSignup = useCallback(
    async e => {
      e.preventDefault();
      if (authForm.password !== authForm.confirmPassword) {
        showError('Passwords do not match');
        return { success: false };
      }

      // Generate temporary username to avoid race conditions
      const tempUsername = `user_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

      const { data: authData, error } = await supabase.auth.signUp({
        email: authForm.email,
        password: authForm.password,
        options: {
          data: {
            username: tempUsername,
            display_name: tempUsername,
            is_public: authForm.is_public,
          },
        },
      });

      if (error) {
        console.error('Signup error:', error);
        showError('Account creation failed. Please try again.');
        return { success: false };
      } else {
        // Profile is automatically created by handle_new_user database trigger

        setAuthForm({
          email: authForm.email,
          password: '',
          confirmPassword: '',
          username: '',
          bio: '',
          location: '',
          seller_terms: 'Standard marketplace terms apply. Contact seller for specific details.',
          is_public: true,
          show_collection_count: true,
          avatar_url: '',
        });
        setAuthMode('login');
        return { success: true };
      }
    },
    [supabase, authForm, setAuthForm, setAuthMode, showError, showSuccess]
  );

  const handleLogout = useCallback(async () => {
    logger.userAction('Logout initiated');

    // Force clear the user state immediately
    setCurrentUser(null);
    setIsAdmin(false);

    try {
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();

      // Don't throw error if already signed out
      if (error && error.message !== 'Auth session missing!') {
        console.error('Logout error:', error);
      }

      logger.authEvent('Logout completed');
    } catch (err) {
      console.error('Logout catch error:', err);
    }

    // Clear any remaining auth data from localStorage
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.includes('auth-token')) {
          localStorage.removeItem(key);
        }
      });
    } catch (storageErr) {
      console.error('Error clearing localStorage:', storageErr);
    }

    // Navigate to home page
    window.location.href = '/';
  }, [supabase]);

  // Modal control functions
  const openAuthModal = useCallback((mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    // Reset form when closing
    setAuthForm({ email: '', password: '', confirmPassword: '' });
  }, []);

  const switchAuthMode = useCallback(mode => {
    setAuthMode(mode);
    // Clear password fields and profile fields when switching modes
    setAuthForm(prev => ({
      ...prev,
      password: '',
      confirmPassword: '',
      username: '',
      bio: '',
      location: '',
      seller_terms: 'Standard marketplace terms apply. Contact seller for specific details.',
      is_public: true,
      show_collection_count: true,
      avatar_url: '',
    }));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // Auth state
      currentUser,
      isAdmin,
      loading,

      // Form state
      authForm,
      setAuthForm,

      // Modal state
      showAuthModal,
      authMode,

      // Auth actions
      handleLogin,
      handleSignup,
      handlePasswordReset,
      handleLogout,
      checkAdminStatus,
      shouldShowProfileCompletion,
      markProfileCompletionShown,

      // Modal actions
      openAuthModal,
      closeAuthModal,
      switchAuthMode,

      // Supabase instance
      supabase,
    }),
    [
      currentUser,
      isAdmin,
      loading,
      authForm,
      setAuthForm,
      showAuthModal,
      authMode,
      handleLogin,
      handleSignup,
      handlePasswordReset,
      handleLogout,
      checkAdminStatus,
      shouldShowProfileCompletion,
      markProfileCompletionShown,
      openAuthModal,
      closeAuthModal,
      switchAuthMode,
      supabase,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});
